import type { DragEvent } from 'react';

/**
 * Dragging a row onto a folder.
 *
 * Four kinds of thing sit in one listing — folders, sheets, documents and
 * uploaded files — and each is a different row component with a different move
 * endpoint. They all have to be draggable onto the same folder row, so the
 * payload is defined once here rather than four times in slightly different
 * shapes.
 *
 * A custom MIME type rather than `text/plain`: it keeps the drop target from
 * accepting a selection of text dragged out of a cell, and keeps a row dragged
 * out of the window from pasting an id into whatever it lands on.
 */
export const DRAG_TYPE = 'application/x-aq-drive-item';

export type DragKind = 'folder' | 'file' | 'spreadsheet' | 'document';

export interface DragPayload {
  kind: DragKind;
  id: string;
  name: string;
}

export function startDrag(event: DragEvent, payload: DragPayload): void {
  event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload));
  event.dataTransfer.effectAllowed = 'move';
}

/**
 * What is being dragged, if it is one of ours.
 *
 * Returns null during `dragover`, where the browser deliberately hides the
 * payload — only the *types* are readable there, which is why `isDriveDrag`
 * exists separately.
 */
export function readDrag(event: DragEvent): DragPayload | null {
  const raw = event.dataTransfer.getData(DRAG_TYPE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DragPayload;
    return parsed && typeof parsed.id === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/** Whether this drag carries one of our rows, readable during dragover. */
export function isDriveDrag(event: DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes(DRAG_TYPE);
}
